import { Link } from "react-router-dom";
import { Plus, Heart, Loader2 } from "lucide-react";
import { useState } from "react";
import ProductImage from "./ProductImage";

/**
 * ProductCard — compact tile designed for a 2-col mobile grid and 4-5 col
 * desktop. Quick-adds (small gold +) only when the product has no required
 * modifiers; otherwise the + button routes to the product detail page so
 * customers can pick their sides / options.
 */
export function ProductCard({
  product,
  onAddToCart,
  favorited = false,
  onFavoriteToggle,
}) {
  const [busy, setBusy] = useState(false);
  const {
    product_id, name, description, price,
    image_url, gallery_images, modifier_groups, add_ons,
  } = product;

  const previewImg = image_url || (gallery_images && gallery_images[0]);
  const hasRequiredModifiers =
    Array.isArray(modifier_groups) && modifier_groups.some((g) => g.required);
  const supportsQuickAdd = !hasRequiredModifiers;

  const handleAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!supportsQuickAdd || busy) return;
    setBusy(true);
    try {
      await Promise.resolve(onAddToCart?.(product));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Link
      to={`/product/${product_id}`}
      className="group block bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-tbc-gold/40 hover:shadow-md transition-all"
      data-testid={`product-card-${product_id}`}
    >
      <div className="relative">
        <ProductImage
          src={previewImg}
          alt={name}
          aspect="aspect-square"
          testId={`product-card-img-${product_id}`}
        />
        {onFavoriteToggle && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFavoriteToggle(product); }}
            aria-label={favorited ? "Remove from favorites" : "Add to favorites"}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-sm backdrop-blur-sm"
            data-testid={`product-fav-${product_id}`}
          >
            <Heart className={`w-4 h-4 ${favorited ? "fill-red-500 text-red-500" : "text-tbc-navy"}`} strokeWidth={2} />
          </button>
        )}
      </div>

      <div className="p-2.5 sm:p-3.5">
        <h3
          className="font-heading text-sm sm:text-base text-tbc-navy line-clamp-2 leading-tight"
          data-testid={`product-name-${product_id}`}
        >
          {name}
        </h3>
        {description && (
          <p className="hidden sm:block mt-1 text-xs text-gray-500 font-body line-clamp-2">
            {description}
          </p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="font-heading text-sm sm:text-base text-tbc-navy">
            ${Number(price || 0).toFixed(2)}
          </span>
          <button
            type="button"
            onClick={handleAdd}
            aria-label={supportsQuickAdd ? `Add ${name} to cart` : `Customize ${name}`}
            className={
              "w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all shadow-sm " +
              (supportsQuickAdd
                ? "bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy"
                : "bg-tbc-cream border border-tbc-gold/40 text-tbc-navy hover:bg-tbc-gold/20")
            }
            data-testid={`add-to-cart-${product_id}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.5} />}
          </button>
        </div>
      </div>
    </Link>
  );
}

export default ProductCard;
